// src/components/principal/ProfileCard.jsx (v5 – final, warning-free)

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import api from "../../services/api";
import { API_PATHS } from "../../config";
import MobileAppOnboardingCard from "../MobileAppOnboardingCard";

/**
 * Profile display and editing card for principals.
 *
 * Allows editing display name and school hashtag, shows Instagram connection/quota,
 * school ID, total photos, and mobile app onboarding prompt if needed.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.profile - Current principal profile data
 * @param {function} props.onProfileChange - Callback to update parent profile state
 * @param {function} props.onError - Error reporting callback
 * @returns {JSX.Element}
 */
const ProfileCard = ({ profile, onProfileChange, onError }) => {
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(profile.displayName || "");

  const [editingSchoolHashtag, setEditingSchoolHashtag] = useState(false);
  const [newSchoolHashtag, setNewSchoolHashtag] = useState(profile.schoolHashtag || "");

  const [busy, setBusy] = useState(false);

  // Instagram quota state
  const [igQuota, setIgQuota] = useState({
    usage: null,
    total: null,
    remaining: null,
    loading: false,
    error: null,
  });

  const isIGConnected =
    profile.instagramUsername && profile.instagramUsername !== "Not connected";

  const showMobileCard = !profile.hasInstalledMobileApp;

  // Sync local edit fields when profile changes
  useEffect(() => {
    setNewDisplayName(profile.displayName || "");
    setNewSchoolHashtag(profile.schoolHashtag || "");
  }, [profile.displayName, profile.schoolHashtag]);

  // Save display name
  const saveDisplayName = async () => {
    const trimmed = newDisplayName.trim();
    if (!trimmed) {
      onError?.("Display name cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_DISPLAY_NAME, {
        new_display_name: trimmed,
      });
      onProfileChange?.({ ...profile, displayName: trimmed });
      setEditingDisplayName(false);
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update display name.");
    } finally {
      setBusy(false);
    }
  };

  // Hashtag normalization
  const normalizeHashtag = (value = "") => {
    let normalized = value.trim().replace(/\s+/g, "_");
    if (!normalized) return "";
    return normalized.startsWith("#") ? normalized : `#${normalized}`;
  };

  const handleHashtagInput = (e) => {
    // Let the user type naturally; normalize only on Save
    setNewSchoolHashtag(e.target.value);
  };

  // Save hashtag
  const saveHashtag = async () => {
    const normalized = normalizeHashtag(newSchoolHashtag);
    if (!normalized) {
      onError?.("School hashtag cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_SCHOOL_HASHTAG, {
        new_hashtag: normalized,
      });
      onProfileChange?.({ ...profile, schoolHashtag: normalized });
      setEditingSchoolHashtag(false);
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update school hashtag.");
    } finally {
      setBusy(false);
    }
  };

  // Connect Instagram
  const connectInstagram = async () => {
    try {
      const { data } = await api.get(API_PATHS.INSTAGRAM_LOGIN);
      if (data.url) window.location.href = data.url;
    } catch (e) {
      onError?.(
        e.response?.data?.detail || "Could not connect to Instagram. Please try again."
      );
    }
  };

  // Load Instagram quota whenever connection status changes
  useEffect(() => {
    if (!isIGConnected) {
      setIgQuota({ usage: null, total: null, remaining: null, loading: false, error: null });
      return;
    }

    const fetchQuota = async () => {
      setIgQuota((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const { data } = await api.get(API_PATHS.INSTAGRAM_QUOTA);
        setIgQuota({
          usage: data.quota_usage ?? null,
          total: data.quota_total ?? null,
          remaining: data.remaining ?? null,
          loading: false,
          error: null,
        });
      } catch (e) {
        setIgQuota((prev) => ({
          ...prev,
          loading: false,
          error: e.response?.data?.detail || "Could not read Instagram quota.",
        }));
      }
    };

    fetchQuota();
  }, [isIGConnected]);

  // Mark mobile app installed
  const handleMobileDownload = async () => {
    try {
      await api.post(API_PATHS.MARK_MOBILE_INSTALLED);
      onProfileChange?.({ ...profile, hasInstalledMobileApp: true });
    } catch (e) {
      console.error("Failed to mark mobile app installed:", e);
      // Silent fail — not critical
    }
  };

  return (
    <div className="principal-panel profile-content">
      {/* Email */}
      <p>
        <strong>Email:</strong>{" "}
        <span className="profile-value">{profile.email}</span>
      </p>

      {/* Display Name */}
      <p>
        <strong>Display Name:</strong>{" "}
        {editingDisplayName ? (
          <>
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              maxLength={255}
              placeholder="e.g. Ms. Smith"
              className="displayname-input"
              autoFocus
            />
            <span className="profile-actions">
              <button
                type="button"
                onClick={saveDisplayName}
                disabled={busy}
                className="btn btn-primary"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingDisplayName(false);
                  setNewDisplayName(profile.displayName || "");
                }}
                className="btn btn-outline"
                disabled={busy}
              >
                Cancel
              </button>
            </span>
          </>
        ) : (
          <>
            <span className="profile-value">{profile.displayName}</span>{" "}
            {profile.displayName === (profile.email?.split("@")[0] || "") ? (
              <button
                type="button"
                onClick={() => setEditingDisplayName(true)}
                className="btn btn-outline profile-nudge-button"
              >
                Click to Change your Display Name
                <span className="nudge-arrow" title="Personalize your name!">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditingDisplayName(true)}
                className="btn btn-outline"
              >
                Edit
              </button>
            )}
          </>
        )}
      </p>

      {/* School */}
      <p>
        <strong>School:</strong>{" "}
        <span className="profile-value">{profile.schoolName}</span>
      </p>

      {/* School Hashtag */}
      <p>
        <strong>School Hashtag:</strong>{" "}
        {editingSchoolHashtag ? (
          <>
            <input
              type="text"
              value={newSchoolHashtag}
              onChange={handleHashtagInput}
              maxLength={50}
              placeholder="e.g. #OurSchool"
              className="hashtag-input"
              autoFocus
            />
            <span className="profile-actions">
              <button
                type="button"
                onClick={saveHashtag}
                disabled={busy}
                className="btn btn-primary"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingSchoolHashtag(false);
                  setNewSchoolHashtag(profile.schoolHashtag || "");
                }}
                className="btn btn-outline"
                disabled={busy}
              >
                Cancel
              </button>
            </span>
          </>
        ) : (
          <>
            <span className="profile-value">
              {profile.schoolHashtag || "Not set"}
            </span>{" "}
            <button
              type="button"
              onClick={() => setEditingSchoolHashtag(true)}
              className="btn btn-outline profile-nudge-button"
            >
              {profile.schoolHashtag ? "Edit" : "Click to set your School Hashtag"}
              {!profile.schoolHashtag && (
                <span className="nudge-arrow" title="Set your hashtag!">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </span>
              )}
            </button>
          </>
        )}
      </p>

      {/* Total Photos */}
      <p>
        <strong>Total Photos:</strong>{" "}
        <span className="profile-value">{profile.totalPhotos}</span>
      </p>

      {/* Instagram */}
      <p>
        <strong>School Instagram Account:</strong>{" "}
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <span className="profile-value">
            {isIGConnected ? `@${profile.instagramUsername}` : "Not connected"}
          </span>
          {!isIGConnected && (
            <button
              type="button"
              className="btn btn-outline instagram-connect-button"
              onClick={connectInstagram}
            >
              Connect to Instagram
            </button>
          )}
        </span>
      </p>

      {/* Instagram Quota */}
      {isIGConnected && (
        <div className="quota-panel">
          <div className="quota-panel-main">
            <strong>Instagram Publishing Quota</strong>
            <div className="quota-panel-text">
              {igQuota.loading && <span>Checking quota…</span>}
              {!igQuota.loading && igQuota.remaining !== null && (
                <span>
                  Used <strong>{igQuota.usage}</strong> of{" "}
                  <strong>{igQuota.total}</strong> in last 24h
                  {" | "}Remaining: <strong>{igQuota.remaining}</strong>
                </span>
              )}
              {!igQuota.loading && igQuota.remaining === null && (
                <span className="text-muted">
                  {igQuota.error || "Quota unavailable right now."}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!isIGConnected) return;
              setIgQuota((prev) => ({ ...prev, loading: true, error: null }));
              try {
                const { data } = await api.get(API_PATHS.INSTAGRAM_QUOTA);
                setIgQuota({
                  usage: data.quota_usage ?? null,
                  total: data.quota_total ?? null,
                  remaining: data.remaining ?? null,
                  loading: false,
                  error: null,
                });
              } catch (e) {
                setIgQuota((prev) => ({
                  ...prev,
                  loading: false,
                  error: e.response?.data?.detail || "Could not refresh quota.",
                }));
              }
            }}
            disabled={igQuota.loading}
            className="btn btn-outline"
          >
            {igQuota.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      )}

      {/* School ID */}
      <p>
        <strong>School ID:</strong>{" "}
        <span className="profile-value">
          {profile.schoolId || "No school ID assigned"}
        </span>
      </p>

      {/* Mobile App Onboarding */}
      {showMobileCard && (
        <>
          <hr style={{ margin: "1.5rem 0 1rem", borderColor: "rgba(148,163,184,0.4)" }} />
          <MobileAppOnboardingCard
            variant="principal"
            storageKey="gg.principal.mobileOnboarding.dismissed"
            onDownload={handleMobileDownload}
          />
        </>
      )}
    </div>
  );
};

export default ProfileCard;